<template>
	<div class="my-3">
		<div
			class="bg-white cursor-pointer prestation-wrapper"
			:class="isSelected ? 'selected outline-hover' : ''"
			@click.stop="handleOpenPopup"
		>
			<div
				class="d-flex d-md-flex flex-column-reverse flex-md-row align-items-start"
			>
				<!-- only desktop -->
				<PrestationPictureDesktop
					:src="image"
					:isDisplayPlaceholders="isDisplayPlaceholders"
					:selected="isSelected"
					@plusInfosClick="handleOpenPopup"
				/>
				<!-- / only desktop -->
				<div class="col-md-9 col-lg-10 px-0 ps-md-4 pe-md-3">
					<DetailsTop
						@plusInfosClick="handleOpenPopup"
						:prestation="props.prestation"
						:image="image"
						:isDisplayPlaceholders="isDisplayPlaceholders"
					/>
					<template v-if="isDisplayPlaceholders">
						<BorderHorizontal />
						<PrestVariantDefault
							:variant="null"
							:isDisplayPlaceholders="isDisplayPlaceholders"
						/>
					</template>

					<template v-else-if=!store.state.activePrestations>
						<div
							v-for="(variant, variant_i) in variants"
							:key="`${variant_i}-${variant.id}`" 
						>
							<BorderHorizontal />

							<component
								:is="getPrestComponent(variant)"
								:variant="variant"
								@select="handleSelect"
								@dropdownClick="handleDropdownClick"
								@clear="handleClear"
								@dropdownSelect="handleDropdownSelect"
							></component>
						</div>
					</template>
				</div>
			</div>
		</div>

		<PopupBase
			:open="isPopupOpen"
			@closePopup="handleClosePopup"
			:overlay-color="'rgba(13, 20, 27, 0.95)'"
			:overlay-max-opacity="1"
		>
			<PrestationPopup
				:open="isPopupOpen"
				:prestation="getPrestation()"
				@closePopup="handleClosePopup"
				@select="
					(id) => {
						handleSelect(id);
						handleClosePopup();
					}
				"
				@dropdownClick="(obj) => handleDropdownClick(obj)"
				@clear="(id) => handleClear(id)"
				@dropdownSelect="
					({ newSelected, id }) => handleDropdownSelect({ newSelected, id })
				"
			/>
		</PopupBase>
	</div>
</template>

<script lang="ts" setup>
import {
	defineProps,
	ref,
	Ref,
	computed,
	ComputedRef,
	withDefaults,
	Component,
	watch
} from "vue";
//import store from "@/vue/planning/store/store";
import PrestationPopup from "@/vue/planning/components/result/prestationPopup.vue";
import PrestationPictureDesktop from "@/vue/planning/components/result/prestation/pictureDesktop.vue";
import BorderHorizontal from "@/vue/common/borderHorizontal.vue";
import DetailsTop from "@/vue/planning/components/result/prestation/detailsTop.vue";
import PrestVariantDefault from "@/vue/planning/components/result/prestation/PrestVariantDefault.vue";
import PrestVariantPierreVacance from "@/vue/planning/components/result/prestation/PrestVariantPierreVacance.vue";
import PopupBase from "@/vue/common/popupBase.vue";
import { isPierreVacance } from "@/vue/planning/logic/pv";
import { sortVariantsBySkipassAndPrice } from "@/vue/planning/logic/updateVariants";

import { PrestationData, Variant } from "@/types";

import { useStore } from "vuex"
const store = useStore();

interface Props {
	prestation: PrestationData;
	activePrestation?: PrestationData;
}
const props = withDefaults(defineProps<Props>(), {
	prestation: () => ({} as PrestationData),
	activePrestation: () => ({} as PrestationData)
});

// refs
const isPopupOpen: Ref<boolean> = ref(false);

// data state
const data = ref({
	popUpWasOpened: false
});

// computed

const image: ComputedRef<string> = computed(() => {
	const images = props.prestation?.picturesAccomodation || [];
	if (images.length === 0) return "";

	const img = images[0];
	return (
		img["imgSmall"] ||
		img["imgMedium"] ||
		img["imgLarge"] ||
		img["imgXL"] ||
		img["img"] ||
		""
	);
});
const isDisplayPlaceholders: ComputedRef<boolean> = computed(() => {
	return Object.keys(props.prestation).length === 0;
});
const isSelected: ComputedRef<boolean> = computed(() => {
	if (!props.prestation || !props.prestation.variants) return false;
	for (const variant of props.prestation.variants) {
		if (store.state.selectedPrestIds[variant.id]) return true;
	}
	return false;
});

const variants: ComputedRef<Variant[]> = computed(() => {
	return sortVariantsBySkipassAndPrice(props.prestation.variants || []);
});

// methods
const getPrestComponent = (variant: any): Component => {
	if (isPierreVacance(variant)) {
		return PrestVariantPierreVacance;
	}
	return PrestVariantDefault;
};
const handleClosePopup = () => {
	isPopupOpen.value = false;
};

const handleOpenPopup = () => {
	isPopupOpen.value = true;
};

const handleSelect = (id: string) => {
	console.log('[prestation.vue] handleSelect called with id:', id);
	// Removed skipass duration check to let Vuex handle validation
	const obj: { [key: string]: number } = {};
	obj[id] = 1;
	store.dispatch("setSelectedPrestIds", obj);
};

const handleClear = (id: string) => {
	const obj: { [key: string]: number } = {};
	obj[id] = 0;
	store.dispatch("setSelectedPrestIds", obj);
};

const handleDropdownSelect = (something: any) => {
	const { newSelected, id } = something;
	const obj: { [key: string]: number } = {};
	obj[id] = newSelected;
	store.dispatch("setSelectedPrestIds", obj);
};

const handleDropdownClick = (obj: any) => {
	if (store.state.selectDropdownOpen_prestId === obj.id) {
		obj["id"] = "";
	}
	store.dispatch("setSelectDropdownOpen_prestId", obj);
};

const getPrestation=(isActivePrestation?:boolean): PrestationData => {
	if(!isActivePrestation) return props.prestation
	return props.activePrestation
}

const handleactivePrestation=():void=> {
	if(store.state.activePrestations) {
		const isActivePrestation: boolean = store.state.activePrestations[0] === props.prestation.prestationId

		if(isActivePrestation) {
			getPrestation(isActivePrestation)
			data.value.popUpWasOpened = true;
			// TODO: Refactor the components load/update sequence to eliminate the need for this timeout.
			setTimeout(() => {
					handleOpenPopup()
			}, 1500)
		}
	}
}

watch(
	() => store.state.prestationsFromCatalog,
	() => {
		if (!data.value.popUpWasOpened && store.state.prestationsFromCatalog.length > 0) {
			handleactivePrestation();
		}
	}
);
</script>

<style scoped>
.box-shadow {
	box-shadow: 0px 0px 15px 0px #0d131b1a;
}
/* 
.box-shadow.selected {
	box-shadow: 0px 0px 0px 6px var(--primary-blue);
} */

.prestation-wrapper {
	border-radius: var(--border-radius-secondary);
	padding: 20px;
	border: 2px solid transparent;
}

.prestation-wrapper.selected {
	border: 2px solid var(--primary-blue);
}

.plus-d-infos-hanging-btn-wrapper {
	border: 2px solid transparent;
	border-radius: 0 0 8px 8px;
	padding: 7px 65px;
	transform: translateY(-2px);
}

.plus-d-infos-hanging-btn-wrapper.selected {
	border-left: 2px solid var(--primary-blue);
	border-right: 2px solid var(--primary-blue);
	border-bottom: 2px solid var(--primary-blue);
}

.plus-d-infos-hanging-btn-wrapper.box-shadow {
	box-shadow: 0px 5px 6px 0px #0d131b1a;
}
.plus-d-infos-hanging-btn-wrapper.box-shadow.selected {
	box-shadow: 0px 4px 3px 2px var(--primary-blue);
}

@media (max-width: 768px) {
	.prestation-wrapper {
		padding: 24px 15px;
		display:flex;
		justify-content: center
	}
}
</style>
